import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { secureStorage } from '../utils/secureStorage';

/**
 * RouteErrorBoundary
 *
 * Class-based error boundary that wraps critical calculator routes
 * (`/inheritance`, `/will`, `/`). Its only job is to catch render
 * errors (usually surfacing as a blank white page during initial
 * hydration of complex wizards or from a corrupted localStorage
 * snapshot) and swap them for a helpful recovery screen instead of
 * leaving the user stuck.
 *
 * Recovery affordances:
 *   1. "Reload page" — hard reload, most common cure for hydration/timing bugs.
 *   2. "Reset saved draft" — clears the localStorage key associated with
 *      the failing route so a corrupted snapshot can't keep re-crashing.
 *   3. "Back to home" — deep escape hatch.
 *
 * Props:
 *   - routeName: short human name, e.g. "inheritance calculator"
 *   - storageKey (optional): the localStorage key used by this route's
 *     autosave. When provided, "Reset saved draft" is shown.
 */
const CLASS_ROOT = 'min-h-screen flex items-center justify-center bg-gradient-to-b from-stone-50 via-white to-stone-100 px-4';

export class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface to console + optional analytics beacon.
    console.error('[RouteErrorBoundary]', this.props.routeName, error, info);
    if (typeof window !== 'undefined' && window.gtag) {
      try {
        window.gtag('event', 'route_error', {
          route: this.props.routeName || 'unknown',
          message: String(error?.message || error).slice(0, 140),
        });
      } catch (_e) { /* ignore analytics failure */ }
    }
  }

  handleReload = () => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  handleResetDraft = () => {
    const { storageKey } = this.props;
    if (storageKey && typeof window !== 'undefined') {
      try {
        secureStorage.removeItem(storageKey);
      } catch (_e) { /* ignore quota errors */ }
    }
    this.handleReload();
  };

  handleGoHome = () => {
    if (typeof window !== 'undefined') window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { routeName = 'this page', storageKey } = this.props;
    const msg = String(this.state.error?.message || this.state.error || 'Unknown error').slice(0, 160);

    return (
      <div className={CLASS_ROOT} data-testid="route-error-boundary">
        <div className="max-w-md w-full text-center py-12">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-amber-700" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-[#0D1520] mb-2">
            We hit a snag loading {routeName}
          </h1>
          <p className="text-stone-600 text-sm leading-relaxed max-w-sm mx-auto">
            Your saved data is safe — it stays on this device. A quick reload
            usually resolves this. If it keeps happening, try resetting the
            saved draft below.
          </p>
          <div className="mt-6 flex flex-col gap-2 max-w-xs mx-auto">
            <button
              type="button"
              onClick={this.handleReload}
              data-testid="route-error-reload"
              className="inline-flex items-center justify-center gap-2 bg-[#EDF5F1] hover:bg-[#EDF5F1] text-[#0D1520] rounded-full px-4 py-2.5 text-sm font-semibold transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Reload page
            </button>
            {storageKey && (
              <button
                type="button"
                onClick={this.handleResetDraft}
                data-testid="route-error-reset-draft"
                className="inline-flex items-center justify-center gap-2 border border-stone-300 hover:border-stone-400 text-stone-700 rounded-full px-4 py-2 text-sm transition-colors"
              >
                Reset saved draft &amp; reload
              </button>
            )}
            <button
              type="button"
              onClick={this.handleGoHome}
              data-testid="route-error-home"
              className="inline-flex items-center justify-center gap-2 text-stone-600 hover:text-[#0D1520] rounded-full px-4 py-2 text-xs transition-colors"
            >
              <Home className="w-3.5 h-3.5" />
              Back to home
            </button>
          </div>
          <p className="mt-6 text-[11px] text-stone-400 font-mono break-all leading-relaxed">
            {msg}
          </p>
        </div>
      </div>
    );
  }
}

export default RouteErrorBoundary;
